/**
 * InsertSectionPlugin - insert a designed section without typing shortcode syntax.
 *
 * Adds a toolbar button that lists the sections declared in config/shortcodes.yml,
 * grouped by category. Picking one opens a form built from that section's
 * parameters (real programs and regions in the dropdowns, a media picker for
 * images) and inserts the resulting shortcode.
 *
 * The shortcode - not the rendered HTML - is what gets saved, so the section
 * stays live: change the underlying partial and every page using it updates.
 *
 * Follows the same shape as InsertFormPlugin.js.
 */

import {
    fetchSections,
    serializeShortcode,
    buildDialogItems,
    toDialogData,
    fromDialogData,
    missingRequired
} from '../utils/SectionForm.js';

export class InsertSectionPlugin {
    constructor(editor) {
        this.editor = editor;
        this.categories = null;
    }

    init() {
        this.registerToolbarButton();
        this.registerMenuItem();
    }

    registerToolbarButton() {
        this.editor.ui.registry.addIcon('sectionicon', '<svg width="24" height="24"><path d="M4 4h16a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1Zm0 2v2h16V6H4Zm0 6h7a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-6a1 1 0 0 1 1-1Zm0 2v4h7v-4H4Zm11-2h5a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1h-5a1 1 0 0 1-1-1v-6a1 1 0 0 1 1-1Zm0 2v4h5v-4h-5Z" fill-rule="evenodd"/></svg>');

        this.editor.ui.registry.addButton('insertsection', {
            icon: 'sectionicon',
            tooltip: 'Insert Section',
            onAction: () => this.openPicker()
        });
    }

    registerMenuItem() {
        this.editor.ui.registry.addMenuItem('insertsection', {
            icon: 'sectionicon',
            text: 'Insert Section',
            onAction: () => this.openPicker()
        });
    }

    async openPicker() {
        if (!this.categories) {
            try {
                this.categories = await fetchSections();
            } catch (error) {
                this.notify('Could not load the section list. Please try again.', 'error');
                return;
            }
        }

        const sections = this.categories.flatMap((group) =>
            group.sections.map((section) => ({ ...section, category: group.category }))
        );

        if (sections.length === 0) {
            this.notify('No sections are available to insert.', 'warning');
            return;
        }

        // Grouped so the list reads as Events / People / Content rather than one
        // long alphabetical run.
        const items = this.categories.flatMap((group) => [
            { text: `— ${group.category} —`, value: '', disabled: true },
            ...group.sections.map((section) => ({ text: section.label, value: section.partial }))
        ]);

        const firstSelectable = sections[0].partial;

        this.editor.windowManager.open({
            title: 'Insert Section',
            size: 'normal',
            body: {
                type: 'panel',
                items: [
                    { type: 'selectbox', name: 'partial', label: 'Section', items },
                    { type: 'htmlpanel', html: '<p style="margin:0;color:#666">Choose a section, then set it up on the next screen.</p>' }
                ]
            },
            initialData: { partial: firstSelectable },
            buttons: [
                { type: 'cancel', text: 'Cancel' },
                { type: 'submit', text: 'Next', primary: true }
            ],
            onSubmit: (api) => {
                const partial = api.getData().partial;
                api.close();

                const section = sections.find((s) => s.partial === partial);
                if (section) this.openConfigDialog(section);
            }
        });
    }

    openConfigDialog(section) {
        const schemaParams = section.params || [];

        // Nothing to configure - insert it straight away rather than showing an
        // empty form.
        if (schemaParams.length === 0) {
            this.insert(section.partial, {});
            return;
        }

        this.editor.windowManager.open({
            title: section.label,
            size: 'large',
            body: {
                type: 'panel',
                items: buildDialogItems(schemaParams, { description: section.description })
            },
            initialData: toDialogData(schemaParams, {}),
            buttons: [
                { type: 'cancel', text: 'Cancel' },
                { type: 'submit', text: 'Insert', primary: true }
            ],
            onSubmit: (api) => {
                const params = fromDialogData(schemaParams, api.getData());
                const missing = missingRequired(schemaParams, params);

                if (missing.length > 0) {
                    this.notify(`Please fill in: ${missing.join(', ')}`, 'warning');
                    return;
                }

                api.close();
                this.insert(section.partial, params);
            }
        });
    }

    async insert(partial, params) {
        let shortcode;

        try {
            const result = await serializeShortcode(partial, params);
            if (!result.success) throw new Error(result.error || 'Could not build the section');
            shortcode = result.shortcode;
        } catch (error) {
            this.notify(error.message, 'error');
            return;
        }

        this.editor.focus();
        // On its own line so ShortcodePreviewPlugin swaps it for a block preview.
        this.editor.insertContent(`<p>${shortcode}</p>`);
        this.editor.save();
    }

    notify(text, type = 'info') {
        this.editor.notificationManager.open({ text, type, timeout: 4000 });
    }
}
